import { LocalNotifications } from '@capacitor/local-notifications';
import { PushNotifications } from '@capacitor/push-notifications';
import { App } from '@capacitor/app';
import * as amplitude from '@amplitude/unified';
import Parse from '../parseClient';
import { isNative, isIOS, isAndroid } from './platform';
import { setBadgeCount } from './ios/badge';
import { FCM_SENDER_ID } from './firebase';

const NOTIFICATION_ID_PREFIX = 'wordhit_daily_';
const DAILY_NOTIFICATION_ID = 999; // Fixed numeric ID for daily repeating notification (must be number, not string)
const STORAGE_KEY_REMINDER_TIME = 'wordhit_reminder_time';
const STORAGE_KEY_PERMISSION_ASKED = 'wordhit_notification_permission_asked';
const STORAGE_KEY_REMINDERS_ENABLED = 'wordhit_reminders_enabled';
const STORAGE_KEY_PUSH_PERMISSION_ASKED = 'wordhit_push_permission_asked';
const DEFAULT_REMINDER_HOUR = 20; // 8 PM
const DEFAULT_REMINDER_MINUTE = 0;

const STORAGE_KEY_LAST_PUSH_TOKEN = 'wordhit_last_push_token';
const STORAGE_KEY_NOTIFICATION_LANG = 'wordhit_notification_lang';

/**
 * Device locale for notification copy (not in-app game language).
 * Uses navigator.languages so English device + Dutch game still gets English reminders.
 */
export function getNotificationLanguage() {
  if (typeof navigator === 'undefined') {
    return 'en';
  }
  const candidates = Array.isArray(navigator.languages) && navigator.languages.length > 0
    ? navigator.languages
    : [navigator.language || navigator.userLanguage || 'en'];
  for (const locale of candidates) {
    if (typeof locale === 'string' && locale.trim().toLowerCase().startsWith('nl')) {
      return 'nl';
    }
  }
  return 'en';
}

function getNotificationTexts(langString) {
  const isNl = langString === 'nl';
  return {
    title: 'WordHit',
    body: isNl ? 'Je hebt vandaag nog niet gespeeld!' : "You haven't played yet today!",
  };
}

function getStoredNotificationLang() {
  try {
    return localStorage.getItem(STORAGE_KEY_NOTIFICATION_LANG);
  } catch (error) {
    return null;
  }
}

function storeNotificationLang(langString) {
  try {
    localStorage.setItem(STORAGE_KEY_NOTIFICATION_LANG, langString);
  } catch (error) {
    // ignore
  }
}

async function pendingDailyNotificationMatches(langString) {
  const { hour, minute } = getReminderTime();
  const { body } = getNotificationTexts(langString);

  try {
    const result = await LocalNotifications.getPending();
    const pending = (result.notifications || []).find((n) => n.id === DAILY_NOTIFICATION_ID);
    if (!pending) {
      return false;
    }

    if (pending.body !== body) {
      return false;
    }

    const on = pending.schedule?.on;
    if (on && typeof on.hour === 'number' && typeof on.minute === 'number') {
      return on.hour === hour && on.minute === minute;
    }

    return true;
  } catch (error) {
    console.warn('[Notification] Failed to inspect pending notifications:', error);
    return false;
  }
}

/**
 * Schedule daily reminder only when missing or out of sync (language/time).
 * Avoids cancel+reschedule on every app load, which caused missed or duplicate notifications.
 */
export async function ensureDailyNotificationScheduled(source = 'automatic') {
  const langString = getNotificationLanguage();

  if (!isNative()) {
    return { scheduled: false, error: 'Not native platform' };
  }

  if (!isRemindersEnabled()) {
    return { scheduled: false, error: 'Reminders disabled' };
  }

  const permission = await checkNotificationPermission();
  if (!permission.granted) {
    return { scheduled: false, error: 'Permission not granted' };
  }

  if (hasPlayedToday()) {
    return { scheduled: false, error: 'Already played today' };
  }

  const storedLang = getStoredNotificationLang();
  const langChanged = storedLang && storedLang !== langString;
  const alreadyScheduled = await pendingDailyNotificationMatches(langString);

  if (alreadyScheduled && !langChanged) {
    console.log('[Notification] Daily reminder already scheduled correctly, skipping reschedule');
    return { scheduled: false, skipped: true };
  }

  const result = await scheduleDailyNotification(langString, source);
  if (result.scheduled) {
    storeNotificationLang(langString);
  }
  return result;
}

/** Last push token received from Capacitor (registration event). Used to verify Installation belongs to this device. */
let lastKnownPushToken = null;

/**
 * Returns the last known push token for this device (from the registration event), or null if not yet received.
 * Persisted in localStorage so it survives app restart (registration event does not fire every launch).
 */
export function getLastKnownPushToken() {
  if (lastKnownPushToken) return lastKnownPushToken;
  try {
    const stored = typeof localStorage !== 'undefined' && localStorage.getItem(STORAGE_KEY_LAST_PUSH_TOKEN);
    if (stored) return stored;
  } catch (e) {
    // ignore
  }
  return null;
}

// Re-export isIOS for backward compatibility
export { isIOS } from './platform';

/**
 * Get the stored reminder time (hour and minute)
 * Returns { hour: number, minute: number } or null if not set
 */
export function getReminderTime() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_REMINDER_TIME);
    if (stored) {
      const parsed = JSON.parse(stored);
      return { hour: parsed.hour ?? DEFAULT_REMINDER_HOUR, minute: parsed.minute ?? DEFAULT_REMINDER_MINUTE };
    }
  } catch (error) {
    console.warn('Failed to get reminder time:', error);
  }
  return { hour: DEFAULT_REMINDER_HOUR, minute: DEFAULT_REMINDER_MINUTE };
}

/**
 * Save the reminder time
 */
export function setReminderTime(hour, minute) {
  try {
    localStorage.setItem(STORAGE_KEY_REMINDER_TIME, JSON.stringify({ hour, minute }));
  } catch (error) {
    console.warn('Failed to save reminder time:', error);
  }
}

/**
 * Check if notification permission has been asked before
 */
export function hasAskedForPermission() {
  try {
    return localStorage.getItem(STORAGE_KEY_PERMISSION_ASKED) === 'true';
  } catch (error) {
    return false;
  }
}

/**
 * Mark that permission has been asked
 */
export function markPermissionAsked() {
  try {
    localStorage.setItem(STORAGE_KEY_PERMISSION_ASKED, 'true');
  } catch (error) {
    console.warn('Failed to mark permission as asked:', error);
  }
}

/**
 * Check if reminders are enabled
 */
export function isRemindersEnabled() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY_REMINDERS_ENABLED);
    // Default to true if not set (backward compatibility)
    return stored === null || stored === 'true';
  } catch (error) {
    console.warn('Failed to check if reminders are enabled:', error);
    return true; // Default to enabled
  }
}

/**
 * Set reminders enabled/disabled
 */
export function setRemindersEnabled(enabled) {
  try {
    localStorage.setItem(STORAGE_KEY_REMINDERS_ENABLED, enabled ? 'true' : 'false');
  } catch (error) {
    console.warn('Failed to set reminders enabled state:', error);
  }
}

/**
 * Check if user has played today
 * Returns true if finishedGameidNummer matches today's idNumber
 */
export function hasPlayedToday() {
  try {
    const finishedId = localStorage.getItem('finishedGameidNummer');
    if (!finishedId) return false;
    
    // Calculate today's idNumber
    const fromDate = new Date(2022, 0, 1);
    const currentDate = new Date();
    const utcCurrent = Date.UTC(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      currentDate.getDate()
    );
    const utcFrom = Date.UTC(
      fromDate.getFullYear(),
      fromDate.getMonth(),
      fromDate.getDate()
    );
    const diffInDays = Math.floor((utcCurrent - utcFrom) / (1000 * 60 * 60 * 24));
    const todayIdNumber = diffInDays;
    
    return String(finishedId) === String(todayIdNumber);
  } catch (error) {
    console.warn('Failed to check if played today:', error);
    return false;
  }
}

/**
 * Request notification permission
 * Returns { granted: boolean, error?: string }
 * Works on iOS and Android
 */
export async function requestNotificationPermission() {
  if (!isNative()) {
    return { granted: false, error: 'Not native platform' };
  }

  try {
    const result = await LocalNotifications.requestPermissions();
    const granted = result.display === 'granted';
    markPermissionAsked();
    
    // Track Amplitude event
    try {
      if (granted) {
        amplitude.track('notification_permission_accepted', {});
      } else {
        amplitude.track('notification_permission_rejected', {});
      }
    } catch (amplitudeError) {
      console.warn('Failed to track notification permission event:', amplitudeError);
    }
    
    return { granted };
  } catch (error) {
    console.error('Failed to request notification permission:', error);
    markPermissionAsked();
    
    // Track rejection on error
    try {
      amplitude.track('notification_permission_rejected', { error: error.message });
    } catch (amplitudeError) {
      console.warn('Failed to track notification permission event:', amplitudeError);
    }
    
    return { granted: false, error: error.message };
  }
}

/**
 * Check current notification permission status
 * Works on iOS and Android
 */
export async function checkNotificationPermission() {
  if (!isNative()) {
    return { granted: false };
  }

  try {
    const result = await LocalNotifications.checkPermissions();
    return { granted: result.display === 'granted' };
  } catch (error) {
    console.error('Failed to check notification permission:', error);
    return { granted: false, error: error.message };
  }
}

/**
 * Get all pending/scheduled notifications and log them
 * Works on iOS and Android
 */
export async function logScheduledNotifications() {
  if (!isNative()) {
    return;
  }

  try {
    const result = await LocalNotifications.getPending();
    const notifications = result.notifications || [];
    
    console.log(`[Notification] 📋 Scheduled notifications (${notifications.length} total):`);
    
    if (notifications.length === 0) {
      console.log(`[Notification]   ⚠️ No notifications scheduled`);
      return;
    }
    
    notifications.forEach((notif, index) => {
      const scheduleAt = notif.schedule?.at ? new Date(notif.schedule.at) : null;
      const scheduleTime = scheduleAt ? scheduleAt.toLocaleString() : 'unknown';
      const repeats = notif.schedule?.repeats ? ` (repeats: ${notif.schedule.every || 'unknown'})` : '';
      
      console.log(`[Notification]   ${index + 1}. ID: ${notif.id}`);
      console.log(`[Notification]      - Title: "${notif.title || 'N/A'}"`);
      console.log(`[Notification]      - Body: "${notif.body || 'N/A'}"`);
      console.log(`[Notification]      - Scheduled for: ${scheduleTime}${repeats}`);
    });
  } catch (error) {
    console.warn('[Notification] ⚠️ Failed to get pending notifications:', error);
  }
}

/**
 * Cancel the daily repeating notification (using fixed ID)
 * WARNING: This cancels the ENTIRE repeating notification, not just today's instance.
 * Only use this when reminders are disabled or when rescheduling.
 * Works on iOS and Android
 */
export async function cancelTodaysNotification() {
  if (!isNative()) {
    return;
  }

  try {
    console.log(`[Notification] 🗑️ Cancelling daily repeating notification with ID: ${DAILY_NOTIFICATION_ID}`);
    console.log(`[Notification] ⚠️ WARNING: This cancels the entire repeat, not just today's instance!`);
    await LocalNotifications.cancel({ notifications: [{ id: DAILY_NOTIFICATION_ID }] });
    console.log(`[Notification] ✅ Successfully cancelled daily repeating notification (ID: ${DAILY_NOTIFICATION_ID})`);
  } catch (error) {
    console.warn('[Notification] ❌ Failed to cancel notification:', error);
  }
}

/**
 * Reschedule the daily repeating notification to start from tomorrow
 * This is used when the user plays today - we cancel the current repeat and 
 * reschedule it to start from tomorrow, preserving the repeat behavior.
 * Works on iOS and Android
 */
export async function rescheduleRepeatFromTomorrow(langString = getNotificationLanguage()) {
  if (!isNative()) {
    return { scheduled: false, error: 'Not native platform' };
  }

  try {
    // Check if reminders are enabled
    if (!isRemindersEnabled()) {
      console.log('[Notification] ❌ Reminders are disabled, not rescheduling');
      return { scheduled: false, error: 'Reminders disabled' };
    }

    // Check permission first
    const permission = await checkNotificationPermission();
    if (!permission.granted) {
      console.log('[Notification] ❌ Permission not granted, not rescheduling');
      return { scheduled: false, error: 'Permission not granted' };
    }

    // Get reminder time
    const { hour, minute } = getReminderTime();

    // Cancel the current repeating notification
    console.log(`[Notification] 🔄 Rescheduling repeat notification: canceling current and rescheduling from tomorrow`);
    await cancelTodaysNotification();

    // Wait a bit to ensure cancellation is processed
    await new Promise(resolve => setTimeout(resolve, 200));

    // Double-check that the notification was actually cancelled
    try {
      const pending = await LocalNotifications.getPending();
      const stillPending = pending.notifications?.find(n => n.id === DAILY_NOTIFICATION_ID);
      if (stillPending) {
        console.log(`[Notification] ⚠️ Notification still pending after cancel, cancelling again...`);
        await LocalNotifications.cancel({ notifications: [{ id: DAILY_NOTIFICATION_ID }] });
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    } catch (error) {
      console.warn('[Notification] Failed to check pending notifications:', error);
    }

    // Calculate schedule time for tomorrow
    const scheduleDate = new Date();
    scheduleDate.setDate(scheduleDate.getDate() + 1);
    scheduleDate.setHours(hour, minute, 0, 0);

    const { title, body } = getNotificationTexts(langString);

    console.log(`[Notification] 📅 Rescheduling daily repeating notification from tomorrow:`);
    console.log(`  - ID: ${DAILY_NOTIFICATION_ID} (same ID, new schedule)`);
    console.log(`  - Time: ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
    console.log(`  - Starts from: ${scheduleDate.toLocaleString()}`);
    console.log(`  - Repeat: daily (every day at ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')})`);
    console.log(`  - Title: "${title}"`);
    console.log(`  - Body: "${body}"`);

    // For repeating notifications that start from a specific date,
    // we need to specify the date components. iOS will treat this as
    // "start from this date and repeat daily at the same time"
    await LocalNotifications.schedule({
      notifications: [
        {
          title,
          body,
          id: DAILY_NOTIFICATION_ID, // Same ID, but new schedule starting tomorrow
          schedule: { 
            on: {
              year: scheduleDate.getFullYear(),
              month: scheduleDate.getMonth() + 1, // JavaScript months are 0-indexed, but schedule expects 1-indexed
              day: scheduleDate.getDate(),
              hour: scheduleDate.getHours(),
              minute: scheduleDate.getMinutes(),
            },
            repeats: true,
            every: 'day' // Repeat every day at the same time
          },
          sound: 'default',
          badge: 1,
        },
      ],
    });

    // Set badge manually via App API (iOS only - Android doesn't support badge counts)
    if (isIOS()) {
      await setBadgeCount(1);
    }

    console.log(`[Notification] ✅ Successfully rescheduled daily repeating notification from tomorrow: ${scheduleDate.toLocaleString()}`);
    storeNotificationLang(langString);
    return { scheduled: true };
  } catch (error) {
    console.error('[Notification] ❌ Failed to reschedule notification from tomorrow:', error);
    return { scheduled: false, error: error.message };
  }
}

/**
 * Schedule daily notification for today (if not played yet)
 * Returns { scheduled: boolean, error?: string }
 * @param {string} langString - Device language ('nl' or 'en'); defaults to getNotificationLanguage()
 * @param {string} source - Source of the scheduling ('automatic' or 'manual')
 * Works on iOS and Android
 */
export async function scheduleDailyNotification(langString = getNotificationLanguage(), source = 'automatic') {
  console.log(`[Notification] Attempting to schedule notification (source: ${source})`);
  
  if (!isNative()) {
    console.log('[Notification] ❌ Not native platform, skipping notification scheduling');
    return { scheduled: false, error: 'Not native platform' };
  }

  try {
    // Check if reminders are enabled
    if (!isRemindersEnabled()) {
      console.log('[Notification] ❌ Reminders are disabled, not scheduling notification');
      return { scheduled: false, error: 'Reminders disabled' };
    }

    // Check permission first
    const permission = await checkNotificationPermission();
    if (!permission.granted) {
      console.log('[Notification] ❌ Permission not granted, not scheduling notification');
      return { scheduled: false, error: 'Permission not granted' };
    }

    // Check if already played today
    if (hasPlayedToday()) {
      console.log('[Notification] ❌ User already played today, not scheduling notification');
      return { scheduled: false, error: 'Already played today' };
    }

    // Get reminder time
    const { hour, minute } = getReminderTime();

    // Cancel any existing daily notification first to prevent duplicates
    console.log(`[Notification] 🗑️ Cancelling any existing daily notification before scheduling new one`);
    await cancelTodaysNotification();

    // Calculate schedule time for today
    const scheduleDate = new Date();
    scheduleDate.setHours(hour, minute, 0, 0);
    const isForTomorrow = scheduleDate.getTime() <= Date.now();
    
    // If the time has already passed today, schedule for tomorrow
    if (isForTomorrow) {
      scheduleDate.setDate(scheduleDate.getDate() + 1);
    }

    const { title, body } = getNotificationTexts(langString);

    console.log(`[Notification] 📅 Scheduling daily repeating notification:`);
    console.log(`  - ID: ${DAILY_NOTIFICATION_ID} (fixed, ensures only 1 notification)`);
    console.log(`  - Time: ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
    console.log(`  - Scheduled for: ${scheduleDate.toLocaleString()}`);
    console.log(`  - For: ${isForTomorrow ? 'tomorrow' : 'today'}`);
    console.log(`  - Source: ${source}`);
    console.log(`  - Title: "${title}"`);
    console.log(`  - Body: "${body}"`);
    console.log(`  - Repeat: daily (every day at ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')})`);

    await LocalNotifications.schedule({
      notifications: [
        {
          title,
          body,
          id: DAILY_NOTIFICATION_ID, // Fixed ID ensures only 1 notification exists
          schedule: { 
            on: {
              hour: scheduleDate.getHours(),
              minute: scheduleDate.getMinutes(),
            },
            repeats: true,
            every: 'day' // Repeat every day at the same time
          },
          sound: 'default',
          badge: 1,
        },
      ],
    });

    // Set badge manually via App API (iOS only - Android doesn't support badge counts)
    if (isIOS()) {
      await setBadgeCount(1);
    }

    console.log(`[Notification] ✅ Successfully scheduled notification for ${scheduleDate.toLocaleString()}`);
    storeNotificationLang(langString);
    return { scheduled: true };
  } catch (error) {
    console.error('[Notification] ❌ Failed to schedule notification:', error);
    return { scheduled: false, error: error.message };
  }
}

/**
 * Schedule notification for tomorrow (always, regardless of whether user played today)
 * Used when permission is just granted and we want to ensure a notification is scheduled
 * @param {string} langString - Device language ('nl' or 'en'); defaults to getNotificationLanguage()
 * @param {boolean} useCurrentTimePlusOneHour - If true, use current time + 1 hour instead of reminder time
 * Works on iOS and Android
 */
export async function scheduleNotificationForTomorrow(langString = getNotificationLanguage(), useCurrentTimePlusOneHour = false) {
  console.log(`[Notification] 📅 Scheduling notification for tomorrow (always)`);
  
  if (!isNative()) {
    console.log('[Notification] ❌ Not native platform, skipping notification scheduling');
    return { scheduled: false, error: 'Not native platform' };
  }

  try {
    // Check if reminders are enabled
    if (!isRemindersEnabled()) {
      console.log('[Notification] ❌ Reminders are disabled, not scheduling notification');
      return { scheduled: false, error: 'Reminders disabled' };
    }

    // Check permission first
    const permission = await checkNotificationPermission();
    if (!permission.granted) {
      console.log('[Notification] ❌ Permission not granted, not scheduling notification');
      return { scheduled: false, error: 'Permission not granted' };
    }

    // Cancel any existing daily notification first to prevent duplicates
    console.log(`[Notification] 🗑️ Cancelling any existing daily notification before scheduling new one`);
    await cancelTodaysNotification();

    // Calculate schedule time for tomorrow
    const scheduleDate = new Date();
    scheduleDate.setDate(scheduleDate.getDate() + 1);
    
    let hour, minute;
    if (useCurrentTimePlusOneHour) {
      // Use current time + 1 hour
      const now = new Date();
      hour = now.getHours();
      minute = now.getMinutes();
      // Add 1 hour
      const totalMinutes = hour * 60 + minute + 60;
      hour = Math.floor(totalMinutes / 60) % 24;
      minute = totalMinutes % 60;
      console.log(`[Notification] ⏰ Using current time + 1 hour: ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
    } else {
      // Use reminder time
      const reminderTime = getReminderTime();
      hour = reminderTime.hour;
      minute = reminderTime.minute;
      console.log(`[Notification] ⏰ Using reminder time: ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`);
    }
    
    scheduleDate.setHours(hour, minute, 0, 0);

    const { title, body } = getNotificationTexts(langString);

    console.log(`[Notification] 📅 Scheduling daily repeating notification for tomorrow:`);
    console.log(`  - ID: ${DAILY_NOTIFICATION_ID} (fixed, ensures only 1 notification)`);
    console.log(`  - Time: ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}${useCurrentTimePlusOneHour ? ' (current time + 1 hour)' : ' (reminder time)'}`);
    console.log(`  - Scheduled for: ${scheduleDate.toLocaleString()}`);
    console.log(`  - Title: "${title}"`);
    console.log(`  - Body: "${body}"`);
    console.log(`  - Repeat: daily (every day at ${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')})`);

    await LocalNotifications.schedule({
      notifications: [
        {
          title,
          body,
          id: DAILY_NOTIFICATION_ID, // Fixed ID ensures only 1 notification exists
          schedule: { 
            on: {
              year: scheduleDate.getFullYear(),
              month: scheduleDate.getMonth() + 1, // JavaScript months are 0-indexed, but schedule expects 1-indexed
              day: scheduleDate.getDate(),
              hour: scheduleDate.getHours(),
              minute: scheduleDate.getMinutes(),
            },
            repeats: true,
            every: 'day' // Repeat every day at the same time
          },
          sound: 'default',
          badge: 1,
        },
      ],
    });

    // Set badge manually via App API (iOS only - Android doesn't support badge counts)
    if (isIOS()) {
      await setBadgeCount(1);
    }

    console.log(`[Notification] ✅ Successfully scheduled notification for tomorrow: ${scheduleDate.toLocaleString()}`);
    storeNotificationLang(langString);
    return { scheduled: true };
  } catch (error) {
    console.error('[Notification] ❌ Failed to schedule notification for tomorrow:', error);
    return { scheduled: false, error: error.message };
  }
}

/**
 * Reschedule notification with new time (ALWAYS, regardless of whether user played today)
 * This is used when user manually changes the reminder time in RemindersModal
 * Works on iOS and Android
 */
export async function rescheduleNotification(newHour, newMinute, langString = getNotificationLanguage()) {
  console.log(`[Notification] 🔄 Rescheduling notification to ${String(newHour).padStart(2, '0')}:${String(newMinute).padStart(2, '0')} (ALWAYS)`);
  
  if (!isNative()) {
    console.log('[Notification] ❌ Not native platform, skipping reschedule');
    return { scheduled: false, error: 'Not native platform' };
  }

  try {
    // Check if reminders are enabled
    if (!isRemindersEnabled()) {
      console.log('[Notification] ❌ Reminders are disabled, not rescheduling');
      return { scheduled: false, error: 'Reminders disabled' };
    }

    // Check permission first
    const permission = await checkNotificationPermission();
    if (!permission.granted) {
      console.log('[Notification] ❌ Permission not granted, not rescheduling');
      return { scheduled: false, error: 'Permission not granted' };
    }

    // Cancel existing notification first
    console.log('[Notification] 🗑️ Cancelling existing notification before rescheduling');
    await cancelTodaysNotification();
    
    // Update stored time
    setReminderTime(newHour, newMinute);
    console.log(`[Notification] 💾 Saved new reminder time: ${String(newHour).padStart(2, '0')}:${String(newMinute).padStart(2, '0')}`);

    // Calculate schedule time - always use the new time
    const scheduleDate = new Date();
    scheduleDate.setHours(newHour, newMinute, 0, 0);
    const isForTomorrow = scheduleDate.getTime() <= Date.now();
    
    // If the time has already passed today, schedule for tomorrow
    if (isForTomorrow) {
      scheduleDate.setDate(scheduleDate.getDate() + 1);
    }

    const { title, body } = getNotificationTexts(langString);

    console.log(`[Notification] 📅 Rescheduling daily repeating notification:`);
    console.log(`  - ID: ${DAILY_NOTIFICATION_ID} (fixed, ensures only 1 notification)`);
    console.log(`  - Time: ${String(newHour).padStart(2, '0')}:${String(newMinute).padStart(2, '0')}`);
    console.log(`  - Scheduled for: ${scheduleDate.toLocaleString()}`);
    console.log(`  - For: ${isForTomorrow ? 'tomorrow' : 'today'}`);
    console.log(`  - Title: "${title}"`);
    console.log(`  - Body: "${body}"`);
    console.log(`  - Repeat: daily (every day at ${String(newHour).padStart(2, '0')}:${String(newMinute).padStart(2, '0')})`);

    await LocalNotifications.schedule({
      notifications: [
        {
          title,
          body,
          id: DAILY_NOTIFICATION_ID, // Fixed ID ensures only 1 notification exists
          schedule: { 
            on: {
              hour: scheduleDate.getHours(),
              minute: scheduleDate.getMinutes(),
            },
            repeats: true,
            every: 'day' // Repeat every day at the same time
          },
          sound: 'default',
          badge: 1,
        },
      ],
    });

    // Set badge manually via App API (iOS only - Android doesn't support badge counts)
    if (isIOS()) {
      await setBadgeCount(1);
    }

    console.log(`[Notification] ✅ Successfully rescheduled notification for ${scheduleDate.toLocaleString()}`);
    storeNotificationLang(langString);
    return { scheduled: true };
  } catch (error) {
    console.error('[Notification] ❌ Failed to reschedule notification:', error);
    return { scheduled: false, error: error.message };
  }
}

/**
 * Compare two version strings (e.g., "1.2.3" vs "1.2.4")
 * Returns: -1 if version1 < version2, 0 if equal, 1 if version1 > version2
 */
function compareVersions(version1, version2) {
  const v1Parts = version1.split('.').map(Number);
  const v2Parts = version2.split('.').map(Number);
  
  const maxLength = Math.max(v1Parts.length, v2Parts.length);
  
  for (let i = 0; i < maxLength; i++) {
    const v1Part = v1Parts[i] || 0;
    const v2Part = v2Parts[i] || 0;
    
    if (v1Part < v2Part) return -1;
    if (v1Part > v2Part) return 1;
  }
  
  return 0;
}

/**
 * Check if app update is required (iOS and Android)
 * Queries Parse Settings for "type" = "updateApp" and checks if current version < requiredAppVersion (iOS) or requiredAppVersionAndroid (Android)
 * Returns: { updateRequired: boolean, requiredVersion?: string, error?: string }
 */
export async function checkAppUpdateRequired() {
  // Only check on native platforms
  if (!isNative()) {
    return { updateRequired: false };
  }

  try {
    // Get current app version
    const appInfo = await App.getInfo();
    const currentVersion = appInfo.version || '0.0.0';
    
    if (!Parse || !Parse.Object) {
      console.warn('[App Update] Parse is not ready');
      return { updateRequired: false, error: 'Parse not ready' };
    }

    // Query Parse Settings for updateApp
    const Settings = Parse.Object.extend("Settings");
    const query = new Parse.Query(Settings);
    query.equalTo("type", "updateApp");
    
    const result = await query.first();
    
    if (!result) {
      // No updateApp setting found, no update required
      return { updateRequired: false };
    }
    
    // Get the appropriate required version based on platform
    let requiredVersion;
    if (isIOS()) {
      requiredVersion = result.get("requiredAppVersion");
    } else if (isAndroid()) {
      requiredVersion = result.get("requiredAppVersionAndroid");
    } else {
      // Fallback for web or unknown platforms
      return { updateRequired: false };
    }
    
    // If requiredVersion is empty or not set, no update required
    if (!requiredVersion || requiredVersion.trim() === '') {
      return { updateRequired: false };
    }
    
    // Compare versions
    const comparison = compareVersions(currentVersion, requiredVersion);
    
    if (comparison < 0) {
      // Current version is less than required version
      const platform = isIOS() ? 'iOS' : 'Android';
      console.log(`[App Update] Update required (${platform}): current=${currentVersion}, required=${requiredVersion}`);
      return { updateRequired: true, requiredVersion: requiredVersion };
    }
    
    // Current version is equal or greater than required version
    return { updateRequired: false };
  } catch (error) {
    console.error('[App Update] Error checking update requirement:', error);
    // On error, don't block the app - assume no update required
    return { updateRequired: false, error: error.message };
  }
}

// Global flag to track if listeners have been set up
let pushNotificationListenersSetup = false;

/**
 * If the app was opened by tapping a battle turn notification, returns the battleId and clears the stored value.
 * Call this when WordHit mounts to open the battle if the user tapped the notification (e.g. app was in background).
 * @returns {string|null} battleId or null
 */
export function getPendingBattleIdFromNotification() {
  try {
    if (typeof sessionStorage === 'undefined') return null;
    const id = sessionStorage.getItem('wordhit_pending_battle_id');
    if (id) {
      sessionStorage.removeItem('wordhit_pending_battle_id');
      return id;
    }
  } catch (e) {
    console.warn('[Push Notification] getPendingBattleIdFromNotification:', e);
  }
  return null;
}

/**
 * iOS only: read pending battle ID from native UserDefaults (set when user tapped a battle push).
 * Returns and clears the value so the battle opens once. Use when app loads so we don't miss the event.
 * @returns {Promise<string|null>} battleId or null
 */
export async function getPendingBattleIdFromNative() {
  if (!isNative() || !isIOS()) {
    console.log("[Push Notification] getPendingBattleIdFromNative: skipped (not iOS native)", { isNative: isNative(), isIOS: isIOS() });
    return null;
  }
  try {
    const { registerPlugin } = await import('@capacitor/core');
    const WordHitNotification = registerPlugin('WordHitNotification');
    const result = await WordHitNotification.getPendingBattleId();
    const id = result?.battleId;
    console.log("[Push Notification] getPendingBattleIdFromNative: raw result", result, "id:", id);
    if (id != null && String(id).trim() !== '' && String(id) !== 'null') {
      console.log('[Push Notification] getPendingBattleIdFromNative: got battleId from native', id);
      return String(id).trim();
    }
    return null;
  } catch (e) {
    console.warn('[Push Notification] getPendingBattleIdFromNative error:', e);
    return null;
  }
}

/**
 * iOS only: clear pending battle ID in UserDefaults (call after opening battle from notification so we don't reopen on next launch).
 */
export async function clearPendingBattleIdFromNative() {
  if (!isNative() || !isIOS()) return;
  try {
    const { registerPlugin } = await import('@capacitor/core');
    const WordHitNotification = registerPlugin('WordHitNotification');
    await WordHitNotification.clearPendingBattleId();
  } catch (e) {
    console.warn('[Push Notification] clearPendingBattleIdFromNative:', e);
  }
}

/**
 * Setup push notification listeners globally (call this once at app start)
 * This ensures we don't miss the registration event
 */
export function setupPushNotificationListeners() {
  console.log('[Push Notification] setupPushNotificationListeners called');
  console.log('[Push Notification] isNative():', isNative());
  console.log('[Push Notification] pushNotificationListenersSetup:', pushNotificationListenersSetup);
  
  if (pushNotificationListenersSetup) {
    console.log('[Push Notification] Listeners already setup');
    return;
  }

  if (!isNative()) {
    console.log('[Push Notification] Not native platform, skipping listener setup');
    return;
  }

  console.log('[Push Notification] Setting up global listeners...');

  // Helper function to save Installation
  const saveInstallationToParse = async (deviceToken) => {
    if (!deviceToken) {
      console.warn('[Push Notification] No device token provided');
      return;
    }
    
    console.log('[Push Notification] Saving Installation for token:', deviceToken);
    
    try {
      // Get app info for Installation properties
      let appIdentifier = 'com.wordhit.app'; // Default fallback
      let appVersion = '1.0.0'; // Default fallback
      try {
        const appInfo = await App.getInfo();
        if (appInfo && appInfo.id) {
          appIdentifier = appInfo.id;
        }
        if (appInfo && appInfo.version) {
          appVersion = appInfo.version;
        }
      } catch (appInfoError) {
        console.warn('[Push Notification] Could not get app info:', appInfoError);
      }
      
      // Call cloud function to create/update Installation
      // This will update existing Installation with "pending" token to the real token
      // Back4App: pass GCMSenderId for Android so the Installation has it set (required for FCM)
      const result = await Parse.Cloud.run('saveInstallation', {
        deviceToken: deviceToken,
        deviceType: isIOS() ? 'ios' : isAndroid() ? 'android' : 'unknown',
        appIdentifier: appIdentifier,
        appVersion: appVersion,
        ...(isAndroid() && FCM_SENDER_ID ? { gcMSenderId: FCM_SENDER_ID } : {})
      });
      
      console.log('[Push Notification] ✅ Installation saved/updated via cloud function:', result);
      console.log('[Push Notification] ✅ Real device token has been saved!');
      return result;
    } catch (error) {
      console.error('[Push Notification] ❌ Error creating/updating Installation:', error);
      throw error;
    }
  };

  // Add listeners for push notification events
  // On iOS: AppDelegate saves Installation via Cloud Function when it receives the device token.
  // On Android: We save the FCM token from JS when Capacitor fires the registration event.
  PushNotifications.addListener('registration', async (token) => {
    console.log('[Push Notification] ✅ Device registered for push (Capacitor event):', token.value);
    lastKnownPushToken = token.value || null;
    try {
      if (lastKnownPushToken && typeof localStorage !== 'undefined') {
        localStorage.setItem(STORAGE_KEY_LAST_PUSH_TOKEN, lastKnownPushToken);
      }
    } catch (e) {
      // ignore
    }
    if (isAndroid()) {
      try {
        await saveInstallationToParse(token.value);
      } catch (err) {
        console.error('[Push Notification] Failed to save Installation on Android:', err);
      }
    } else {
      console.log('[Push Notification] iOS: AppDelegate saves Installation, skipping JS save');
    }
  });
  
  PushNotifications.addListener('registrationError', (error) => {
    console.error('[Push Notification] ❌ Registration error:', error);
  });

  // When user taps a push notification (e.g. battle turn), notify the app to open the corresponding battle
  PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const notif = action.notification;
    // Payload can be in .data, .additionalData, or (iOS/Parse) nested in .customData or top-level
    const data = notif?.data || notif?.additionalData || notif?.customData || {};
    const type = data.type || data.Type;
    const battleId = data.battleId || data.battle_id;
    console.log('[Push Notification] pushNotificationActionPerformed:', { type, battleId, dataKeys: Object.keys(data || {}) });
    if (battleId && (type === 'battle_turn' || !type)) {
      const id = String(battleId).trim();
      if (!id) return;
      console.log('[Push Notification] 🎯 Opening battle from notification, battleId:', id);
      try {
        if (typeof sessionStorage !== 'undefined') {
          sessionStorage.setItem('wordhit_pending_battle_id', id);
        }
        if (typeof window !== 'undefined' && window.dispatchEvent) {
          window.dispatchEvent(new CustomEvent('wordhit-open-battle-from-notification', { detail: { battleId: id } }));
        }
      } catch (e) {
        console.warn('[Push Notification] Failed to dispatch open-battle:', e);
      }
    }
  });

  // Also listen for when a push is received in foreground (optional: could show in-app banner; for now we only handle tap)
  // PushNotifications.addListener('pushNotificationReceived', ...) if needed later

  // Also listen for manual registration event from AppDelegate (fallback)
  // This handles the case where Capacitor doesn't automatically trigger the registration event
  if (typeof window !== 'undefined') {
    // Note: AppDelegate handles Installation saving directly via Cloud Function
    // We don't need these manual event listeners anymore to avoid conflicts
    window.addEventListener('pushNotificationRegistration', async (event) => {
      console.log('[Push Notification] 📥 Received manual registration event from AppDelegate (ignored - AppDelegate handles saving)');
    });
    
    // Listen for DeviceTokenReceived event from AppDelegate via Capacitor bridge
    document.addEventListener('DeviceTokenReceived', async (event) => {
      console.log('[Push Notification] 📥 Received DeviceTokenReceived event from AppDelegate (ignored - AppDelegate handles saving)');
    });
    
    console.log('[Push Notification] ✅ Added listeners for manual registration events');
  }

  pushNotificationListenersSetup = true;
  console.log('[Push Notification] ✅ Global listeners setup complete');
}

/**
 * Check if push notification permission has been asked before
 */
export function hasAskedForPushPermission() {
  try {
    if (typeof Storage !== 'undefined' && localStorage) {
      return localStorage.getItem(STORAGE_KEY_PUSH_PERMISSION_ASKED) === 'true';
    }
    return false;
  } catch (error) {
    return false;
  }
}

/**
 * Mark that push permission has been asked
 */
export function markPushPermissionAsked() {
  try {
    if (typeof Storage !== 'undefined' && localStorage) {
      localStorage.setItem(STORAGE_KEY_PUSH_PERMISSION_ASKED, 'true');
    } else {
      console.warn('localStorage not available, cannot mark push permission as asked');
    }
  } catch (error) {
    console.warn('Failed to mark push permission as asked:', error);
  }
}

/**
 * Link existing Installation objects to current user
 * This should be called after user login or when push permission is granted.
 * On Android, passes deviceToken + deviceType when available for targeted link; otherwise auto-links recent orphans.
 */
export async function linkInstallationToUser() {
  if (!isNative()) {
    return;
  }

  try {
    const currentUser = Parse.User.current();
    if (!currentUser) {
      console.log('[Push Notification] No user logged in, skipping Installation link');
      return;
    }

    console.log('[Push Notification] Attempting to link Installation to user:', currentUser.id);

    // On Android we have the push token in JS → use targeted link (exact match for this device)
    const params = {};
    if (isAndroid()) {
      const token = getLastKnownPushToken();
      if (token) {
        params.deviceToken = token;
        params.deviceType = 'android';
      }
    }
    // iOS: no params → cloud uses auto-link (recent orphan ios Installations)

    const result = await Parse.Cloud.run('linkInstallationToUser', Object.keys(params).length ? params : undefined);
    console.log('[Push Notification] ✅ Installation link result:', result);
    return result;
  } catch (error) {
    console.error('[Push Notification] Error linking Installation to user:', error);
  }
}

/**
 * Request push notification permission (APN for iOS)
 * Returns { granted: boolean, error?: string }
 * Only works on native platforms (iOS/Android)
 */
export async function requestPushNotificationPermission() {
  if (!isNative()) {
    console.log('[Push Notification] Not native platform, skipping push permission request');
    return { granted: false, error: 'Not native platform' };
  }

  // Check if we've already asked
  if (hasAskedForPushPermission()) {
    console.log('[Push Notification] Permission already asked before, checking current status');
    // Still check current status
    try {
      const status = await PushNotifications.checkPermissions();
      console.log('[Push Notification] Current permission status:', status);
      return { granted: status.receive === 'granted' };
    } catch (error) {
      console.error('[Push Notification] Error checking permissions:', error);
      return { granted: false, error: error.message };
    }
  }

  try {
    // Ensure global listeners are setup (they should be, but just in case)
    // This is especially important if Capacitor wasn't ready when index.js ran
    setupPushNotificationListeners();
    
    console.log('[Push Notification] Calling register()...');
    
    // Register for push notifications (this will trigger the permission dialog on iOS)
    await PushNotifications.register();
    console.log('[Push Notification] PushNotifications.register() completed');
    
    // Request permissions (this should show the permission dialog)
    const result = await PushNotifications.requestPermissions();
    const granted = result.receive === 'granted';
    console.log('[Push Notification] Permission request result:', granted ? 'granted' : 'denied');
    
    // If permission is granted, AppDelegate will automatically:
    // 1. Receive the device token via didRegisterForRemoteNotificationsWithDeviceToken (already triggered by requestPermissions)
    // 2. Save the Installation via the Cloud Function
    // 3. We need to link the Installation to the current user (if logged in)
    if (granted) {
      console.log('[Push Notification] Permission granted');
      console.log('[Push Notification] AppDelegate will automatically receive device token and save Installation');
      console.log('[Push Notification] No need to call register() again - requestPermissions() already triggered registration');
      
      // Immediately link Installation to user after permission is granted
      // AppDelegate will save the Installation, then we link it to the user
      setTimeout(async () => {
        try {
          // Use Cloud Function to link Installation (requires useMasterKey)
          const currentUser = Parse.User.current();
          if (currentUser) {
            console.log('[Push Notification] Linking Installation to user after permission grant...');
            const result = await Parse.Cloud.run('linkInstallationToUser');
            console.log('[Push Notification] ✅ Installation linked to user after permission grant:', result);
          } else {
            console.log('[Push Notification] No user logged in, skipping Installation link');
          }
        } catch (linkError) {
          console.warn('[Push Notification] Failed to link Installation to user:', linkError);
        }
      }, 1500); // Wait 1.5 seconds for AppDelegate to save Installation
    }
    
    markPushPermissionAsked();
    
    // Track Amplitude event
    try {
      if (granted) {
        amplitude.track('push_notification_permission_accepted', {});
      } else {
        amplitude.track('push_notification_permission_rejected', {});
      }
    } catch (amplitudeError) {
      console.warn('Failed to track push notification permission event:', amplitudeError);
    }
    
    console.log('[Push Notification] Permission request result:', granted ? 'granted' : 'denied');
    return { granted };
  } catch (error) {
    console.error('[Push Notification] Failed to request push notification permission:', error);
    markPushPermissionAsked();
    
    // Track rejection on error
    try {
      amplitude.track('push_notification_permission_rejected', { error: error.message });
    } catch (amplitudeError) {
      console.warn('Failed to track push notification permission event:', amplitudeError);
    }
    
    return { granted: false, error: error.message };
  }
}
